/* Copyright (c) 2020-present CloudIO Inc */

import { isEmpty } from '../../../../utils/isEmpty';
import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import {
  baseColumnProps,
  formatFunction,
  isBaseColumnPropertyVisible,
} from '../BaseColumn';
import { defaultValue } from './NumberColumn';
import { analyzeNumberColumn } from './NumberColumnAnalyze';

const NumberColumnManager: ComponentManagerFn<'NumberColumn'> = (
  platform: Platform,
): ComponentManager<'NumberColumn', never> => {
  return {
    ...baseComponentManager<'NumberColumn'>(platform, 'NumberColumn'),
    type: 'NumberColumn',
    name: 'Number Column',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ColumnFooter',
          'EventSubscription',
          'TriggerOnBlur',
          'TriggerOnCellClick',
          'TriggerOnChange',
          'TriggerOnFocus',
          'TriggerOnValueChange',
          'TriggerOnPaste',
          'TriggerOnEnterKeyPress',
          'TriggerOnDeleteKeyPress',
          'TriggerAfterPaste',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        aliasType: ({ appUid, pageId, itemId }) => {
          const parent = platform.findParentOfType(
            appUid,
            pageId,
            itemId,
            'LOVGrid',
          );
          return parent ? 'LOV' : 'STORE';
        },
        getAttributeDataTypes: ({ props }) => [
          'Double',
          'Integer',
          'Decimal',
          'Percent',
        ],
      },
      minValue: {
        label: 'Min Value',
        type: 'numberInput',
        category: 'Validation',
      },
      maxValue: {
        label: 'Max Value',
        type: 'numberInput',
        category: 'Validation',
      },

      allowNegative: {
        label: 'Allow Negative',
        type: 'boolean',
        category: 'Validation',
      },
      maxUniqueVals: {
        label: 'Max Unique Values',
        type: 'numberInput',
        helpText: 'Used to limit unique options for choose value filters',
      },
      applyExcelFormatting: {
        label: 'Apply Excel Formatting',
        type: 'boolean',
        category: 'Display',
      },
      format: {
        label: 'Format',
        type: 'string',
        category: 'Display',
        helpText: 'e.g. #### #### #### #### or ##/## or (###) ###-####',
      },
      mask: {
        label: 'Mask Characters',
        type: 'string',
        category: 'Display',
        helpText:
          'If defined, input will show non entered space with masked value. e.g. MMYY or _. Each character corresponds to the same index #.',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
        category: 'Display',
      },
      allowDecimal: {
        label: 'Allow Decimal',
        type: 'boolean',
        category: 'Display',
      },
      fixedDecimalScale: {
        label: 'Fixed Decimal Scale',
        type: 'boolean',
        category: 'Display',
        helpText: 'If checked it add 0s to match given decimalScale',
      },
      decimalScale: {
        label: 'Decimal Scale',
        type: 'numberInput',
        category: 'Display',
        helpText: 'If defined it limits to given decimal scale',
      },
      thousandSeparator: {
        label: 'Thousand Separator',
        type: 'boolean',
        category: 'Display',
      },
      serverValidateOnChange: {
        category: 'Validation',
        label: 'Server Validate On Change',
        type: 'boolean',
      },
      step: {
        category: 'General',
        label: 'Step',
        type: 'numberInput',
      },
      hideSpinner: {
        category: 'General',
        label: 'Hide Spinner',
        type: 'boolean',
      },
      skipScrollOnChange: {
        category: 'General',
        label: 'Skip Value Change On Scroll',
        type: 'boolean',
        helpText:
          'Check this to skip the value change on scroll in  number column',
      },
      formatFunction,
    },
    isPropertyVisible(
      prop,
      {
        insertable = { type: 'boolean', value: true },
        updatable = { type: 'boolean', value: true },
        allowDecimal = true,
        fixedDecimalScale = false,
        thousandSeparator,
        hideSpinner,
        format,
      },
      { appUid, itemId, pageId },
    ): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      const item = platform.getItem(appUid, pageId, itemId);
      if (!item) {
        platform.showError(`Missing column with ID: ${itemId}!`);
        return false;
      }
      const parent = platform.getItem(appUid, pageId, item.parent);
      if (!parent) {
        platform.showError(`Missing parent item with ID: ${item.parent}!`);
        return false;
      }
      if (parent?.type === 'LOVGrid') {
        switch (prop) {
          case 'minValue':
          case 'maxValue':
          case 'allowDecimal':
          case 'allowNegative':
          case 'placeholder':
            return false;
          default:
        }
      }
      const insertAllowed =
        typeof insertable === 'boolean'
          ? insertable
          : insertable.type === 'boolean'
          ? insertable.value
          : true;
      const updateAllowed =
        typeof updatable === 'boolean'
          ? updatable
          : updatable.type === 'boolean'
          ? updatable.value
          : true;
      switch (prop) {
        case 'placeholder':
        case 'minValue':
        case 'maxValue':
        case 'allowDecimal':
        case 'allowNegative':
          return insertAllowed || updateAllowed;
        case 'decimalScale':
          return fixedDecimalScale || !(insertAllowed || updateAllowed);
        case 'fixedDecimalScale':
          return allowDecimal || !(insertAllowed || updateAllowed);
        case 'mask':
          return (insertAllowed || updateAllowed) && !isEmpty(format);
        case 'step':
        case 'hideSpinner':
          return !fixedDecimalScale && !thousandSeparator && isEmpty(format);
        case 'skipScrollOnChange':
          return (
            !hideSpinner &&
            !fixedDecimalScale &&
            !thousandSeparator &&
            isEmpty(format)
          );
        default:
          return true;
      }
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    analyze: analyzeNumberColumn,
    replaceableWith: [
      'LookupSelectColumn',
      'ProgressIndicatorColumn',
      'SelectColumn',
      'StarColumn',
      'StringColumn',
    ],
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-number-input', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Number Column is a data entry and display column designed specifically for numeric values. It handles Integers, Decimals, Doubles, and Percentages.
It goes beyond simple text entry by enforcing numeric validity (e.g., preventing "abc" input) and providing rich formatting options for currency, percentages, and scientific data. It is essential for financial grids, inventory counts, and metric dashboards.

**1. Data Binding & Types**<br/>
To function correctly, this column must bind to a numeric attribute in your Data Store.
* **View Attribute:** Select the Data Store field.
* **Supported Types:**
    * **Integer:** Whole numbers (e.g., Inventory Count).
    * **Decimal / Double:** Floating-point numbers (e.g., Prices, Weights).
    * **Percent:** Renders 0.5 as 50%.

**2. Formatting & Precision**<br/>
Numeric data often requires strict visual formatting to be readable.
* **Thousand Separator:** Adds commas (e.g., 1,000,000). Essential for financial data.
* **Decimal Scale:** Sets the maximum number of decimal places allowed (e.g., set to 2 for currency).
* **Fixed Decimal Scale:** If enabled, pads the number with zeros to always match the scale (e.g., 5 becomes 5.00).
* **Allow Negative:**
    * **Enabled:** Allows -100.
    * **Disabled:** Restricts input to positive numbers only (useful for "Quantity" fields).
* **Mask / Format:** Apply custom patterns like (###) ###-#### or ##/##.

**3. Input Behavior (UX)**<br/>
* **Step:** Defines the increment value when using the up/down arrows or spinner (e.g., 0.5 increments by half).
* **Hide Spinner:** Hides the small up/down arrows inside the cell for a cleaner look.
* **Skip Scroll On Change:** By default, scrolling the mouse wheel while hovering over a focused number cell changes the value. Enable this to prevent accidental changes during scrolling.

**4. Validation**<br/>
* **Min / Max Value:** Sets hard limits on the input (e.g., Min: 0, Max: 100 for a percentage field).
* **Server Validate On Change:** Triggers a backend check immediately after the value changes. Useful for complex business rules like "Discount cannot exceed Margin."

**5. Best Practices**<br/>
* **Currency:** Use **Thousand Separator: True**, **Decimal Scale: 2**, **Fixed Decimal Scale: True**.
* **ID Fields:** For numeric IDs (like Order ID 10234) that should not have commas, ensure **Thousand Separator** is **Disabled**.`,
  };
};

export default NumberColumnManager;
